const SHIPPING_CALCULATOR_STATES = {
  AC: "Acre",
  AL: "Alagoas",
  AP: "Amapá",
  AM: "Amazonas",
  BA: "Bahia",
  CE: "Ceará",
  DF: "Distrito Federal",
  ES: "Espírito Santo",
  GO: "Goiás",
  MA: "Maranhão",
  MT: "Mato Grosso",
  MS: "Mato Grosso do Sul",
  MG: "Minas Gerais",
  PA: "Pará",
  PB: "Paraíba",
  PR: "Paraná",
  PE: "Pernambuco",
  PI: "Piauí",
  RJ: "Rio de Janeiro",
  RN: "Rio Grande do Norte",
  RS: "Rio Grande do Sul",
  RO: "Rondônia",
  RR: "Roraima",
  SC: "Santa Catarina",
  SP: "São Paulo",
  SE: "Sergipe",
  TO: "Tocantins",
};

const SHIPPING_CALCULATOR_SAME_DAY_METHOD = "Keeko Mesmo Dia (pedidos até 14h)";

// The CEP is shared by every CEP field on the site (PDP, cart drawer, store
// locator): saved in localStorage ("cep", "00000-000") and announced with a
// "novvo:cep-change" window event so fields already on the page follow along.
const SHIPPING_CALCULATOR_CEP_KEY = "cep";

function shippingCalculatorFormatCep(value) {
  return String(value || "").replace(/\D/g, "").slice(0, 8).replace(/^(\d{5})(\d)/, "$1-$2");
}

function shippingCalculatorSavedCep() {
  try {
    return window.localStorage.getItem(SHIPPING_CALCULATOR_CEP_KEY) || "";
  } catch (e) {
    return "";
  }
}

function shippingCalculatorSaveCep(value, source) {
  const cep = shippingCalculatorFormatCep(value);
  try {
    window.localStorage.setItem(SHIPPING_CALCULATOR_CEP_KEY, cep);
  } catch (e) {}
  window.dispatchEvent(new CustomEvent("novvo:cep-change", { detail: { cep, source } }));
}

window.NovvoCep = {
  get: shippingCalculatorSavedCep,
  save: shippingCalculatorSaveCep,
  format: shippingCalculatorFormatCep,
};

class ShippingCalculator extends HTMLElement {
  connectedCallback() {
    this.variantId = this.dataset.variantId || "";
    this.input = this.querySelector("[data-shipping-cep]");
    this.button = this.querySelector("[data-shipping-submit]");
    this.errorEl = this.querySelector("[data-shipping-error]");
    this.resultsEl = this.querySelector("[data-shipping-results]");

    if (this.input) {
      this.input.addEventListener("input", (event) => {
        this.maskCep(event);
        this.syncButton();
      });
      // After a calculation the button stays disabled until the shopper goes
      // back to the CEP field.
      this.input.addEventListener("focus", () => {
        this.locked = false;
        this.syncButton();
      });
      const saved = shippingCalculatorSavedCep();

      if (saved) {
        this.input.value = saved;
      }

      // Another CEP field (e.g. the cart drawer) calculated a new CEP.
      this.onCepChange = (event) => {
        if (event.detail.source === this || document.activeElement === this.input) return;
        if (this.input.value === event.detail.cep) return;
        this.input.value = event.detail.cep;
        this.locked = false;
        this.syncButton();
      };
      window.addEventListener("novvo:cep-change", this.onCepChange);
    }

    if (this.button) {
      this.button.addEventListener("click", () => {
        if (!this.button.disabled) this.calculate();
      });
    }

    this.syncButton();
  }

  // The cart drawer re-renders its calculator on every refresh.
  disconnectedCallback() {
    if (this.onCepChange) window.removeEventListener("novvo:cep-change", this.onCepChange);
  }

  // Own button (PDP) only: enabled with a full 8-digit CEP, while not
  // calculating and not locked after the last calculation.
  syncButton() {
    if (!this.button || !this.input) return;
    const hasCep = this.input.value.replace(/\D/g, "").length === 8;
    this.button.disabled = !!this.calculating || !!this.locked || !hasCep;
  }

  // PDP: the kit changed, so the simulated item changes too. Recalculates if
  // a CEP had already been calculated, so the shown price isn't stale.
  setVariant(variantId) {
    if (!variantId || String(variantId) === String(this.variantId)) return;
    this.variantId = String(variantId);
    if (this.lastCep) this.calculate(this.lastCep);
  }

  maskCep(event) {
    let value = event.target.value.replace(/\D/g, "");
    value = value.replace(/^(\d{5})(\d)/, "$1-$2");
    event.target.value = value;
  }

  setError(message) {
    if (this.errorEl) {
      this.errorEl.textContent = message;
      this.errorEl.hidden = !message;
    }

    if (message) {
      this.dispatchEvent(
        new CustomEvent("shipping-calculator:change", {
          bubbles: true,
          detail: { rates: [], error: message },
        }),
      );
    }
  }

  calculateFor(cep) {
    return this.calculate(cep);
  }

  // One calculation at a time, latest request wins. The PDP prices its item
  // by briefly adding it to the real cart, so overlapping runs (e.g. switching
  // kits quickly) would see each other's item and the slower one could
  // overwrite the newer result. A request made while one is running replaces
  // any queued one; a run that got superseded doesn't show its result.
  async calculate(cepOverride) {
    const requested = cepOverride || this.input?.value || "";
    if (this.running) {
      this.queued = requested;
      return this.running;
    }

    this.running = this.runCalculation(requested);
    try {
      await this.running;
    } finally {
      this.running = null;
      if (this.queued !== undefined) {
        const next = this.queued;
        this.queued = undefined;
        await this.calculate(next);
      }
    }
  }

  async runCalculation(requested) {
    const superseded = () => this.queued !== undefined;

    this.setError("");

    if (this.resultsEl) {
      this.resultsEl.innerHTML = "";
    }

    const rawCep = String(requested).replace(/\D/g, "");

    if (rawCep.length !== 8) {
      this.setError("CEP inválido");
      return;
    }

    this.lastCep = rawCep;
    shippingCalculatorSaveCep(rawCep, this);

    const originalLabel = this.button?.dataset.label || this.button?.textContent;
    if (this.button) this.button.dataset.label = originalLabel;
    this.calculating = true;
    if (this.button) {
      this.button.disabled = true;
      this.button.textContent = "Calculando...";
    }

    try {
      const address = await this.getCepData(rawCep);
      if (superseded()) return;

      if (address.erro) {
        this.setError("CEP não encontrado");
        this.trackResult(rawCep, false);
        return;
      }

      // R$ 0,00 options are left out of estimates (PDP, store locator): it
      // isn't clear they'd really be free. The cart drawer, priced on the
      // real cart, keeps them and labels them "Grátis" (data-free-shipping).
      const showFree = this.dataset.freeShipping === "show";
      const rates = (await this.getShippingRates(address.uf, rawCep)).filter(
        (rate) => showFree || parseFloat(rate.price) > 0,
      );
      if (superseded()) return;

      this.renderResults(rates);
      this.trackResult(rawCep, rates.length > 0);
    } catch (error) {
      if (superseded()) return;
      this.setError("Não foi possível calcular o frete agora.");
      console.error("shipping-calculator", error);
    } finally {
      this.calculating = false;
      this.locked = true;
      if (this.button) {
        this.button.textContent = originalLabel;
      }
      this.syncButton();
    }
  }

  // P2 — calculate_shipping
  // Quando: resultado do 'Calcule o frete' (PDP e carrinho). shipping_found=false
  // quando aparece 'Nenhuma opção de frete encontrada' ou 'CEP não encontrado'.
  // Só os 3 primeiros dígitos do CEP.
  // Opt out with data-tracking="off" where another event already covers
  // the same action (store locator).
  trackResult(cep, found) {
    if (this.dataset.tracking === "off" || !window.NovvoTracking) {
      return;
    }

    window.NovvoTracking.push({
      event: "calculate_shipping",
      cep_prefix: window.NovvoTracking.cepPrefix(cep),
      shipping_found: found,
    });
  }

  async getCepData(cep) {
    const response = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
    return response.json();
  }

  async getShippingRates(uf, zip) {
    const province = SHIPPING_CALCULATOR_STATES[uf] || uf;
    const params = new URLSearchParams({
      "shipping_address[country]": "Brazil",
      "shipping_address[province]": province,
      "shipping_address[zip]": zip,
    });

    if (!this.variantId) {
      const data = await fetch(
        `/cart/shipping_rates.json?${params.toString()}`,
      ).then((r) => r.json());
      return data.shipping_rates || [];
    }

    const added = await fetch("/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ items: [{ id: this.variantId, quantity: 1 }] }),
    }).then((r) => r.json());
    // /cart/add.js with an "items" array always responds with { items: [...] },
    // never a top-level id/quantity — read the added line from there.
    const addedItem =
      added && Array.isArray(added.items) ? added.items[0] : null;

    try {
      const data = await fetch(
        `/cart/shipping_rates.json?${params.toString()}`,
      ).then((r) => r.json());
      return data.shipping_rates || [];
    } finally {
      if (addedItem) {
        // addedItem.quantity is the line's total after the merge, so this
        // restores the line to what it was before (removes it entirely
        // only if it wasn't already in the cart) instead of always
        // wiping the whole line with quantity: 0.
        const restoredQuantity = Math.max(0, addedItem.quantity - 1);
        await fetch("/cart/change.js", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            id: addedItem.key,
            quantity: restoredQuantity,
          }),
        });
      }
    }
  }

  renderResults(rates) {
    if (!this.resultsEl) {
      return;
    }

    if (!rates.length) {
      this.setError("Nenhuma opção de frete encontrada para esse CEP.");
      return;
    }

    this.resultsEl.innerHTML = "";

    rates.forEach((rate) => {
      const row = document.createElement("div");
      row.className = "shipping-calculator__result";

      const name = document.createElement("span");
      name.className = "shipping-calculator__result-name";
      name.textContent =
        rate.name === SHIPPING_CALCULATOR_SAME_DAY_METHOD
          ? "Keeko Express"
          : rate.name;
      row.append(name);

      if (
        Array.isArray(rate.delivery_range) &&
        rate.delivery_range.length > 1
      ) {
        const eta = document.createElement("span");
        eta.className = "shipping-calculator__result-eta";
        eta.textContent = this.formatDeliveryEstimate(
          rate.delivery_range[1],
          rate.name,
        );
        row.append(eta);
      }

      const price = document.createElement("span");
      price.className = "shipping-calculator__result-price";
      price.textContent =
        parseFloat(rate.price) === 0 ? "Grátis" : `R$ ${String(rate.price).replace(".", ",")}`;
      row.append(price);

      this.resultsEl.append(row);
    });

    this.dispatchEvent(
      new CustomEvent("shipping-calculator:change", {
        bubbles: true,
        detail: { rates, error: null },
      }),
    );
  }

  isBlockedDate(date) {
    const blockedDates = [
      { d: 25, m: 12 },
      { d: 30, m: 12 },
      { d: 31, m: 12 },
      { d: 1, m: 1 },
    ];

    return blockedDates.some(
      (item) => item.d === date.getDate() && item.m === date.getMonth() + 1,
    );
  }

  adjustWeekend(date) {
    const weekday = date.getDay();

    if (weekday === 6) {
      date.setDate(date.getDate() + 2);
    }

    if (weekday === 0) {
      date.setDate(date.getDate() + 1);
    }

    return date;
  }

  adjustCarnival2026(date) {
    const year = date.getFullYear();
    const day = date.getDate();
    const month = date.getMonth() + 1;
    if (year === 2026 && month === 2 && [16, 17, 18].includes(day)) {
      return new Date(2026, 1, 19);
    }
    return date;
  }

  getDefaultDate(deliveryDate, shippingMethod) {
    let date = new Date(deliveryDate);
    const now = new Date();
    const currentHour = now.getHours();

    if (
      shippingMethod === SHIPPING_CALCULATOR_SAME_DAY_METHOD &&
      currentHour < 14
    ) {
      while (this.isBlockedDate(date) || [0, 6].includes(date.getDay())) {
        date.setDate(date.getDate() + 1);
      }

      date = this.adjustCarnival2026(date);
      date = this.adjustWeekend(date);
      return date;
    }

    date.setDate(date.getDate() + 1);

    while (this.isBlockedDate(date)) {
      date.setDate(date.getDate() + 1);
    }

    date = this.adjustWeekend(date);
    date = this.adjustCarnival2026(date);

    return date;
  }

  formatShortDate(date) {
    const day = date.getDate().toString().padStart(2, "0");
    const month = (date.getMonth() + 1).toString().padStart(2, "0");

    return `${day}/${month}`;
  }

  formatDeliveryEstimate(deliveryDate, shippingMethod) {
    const defaultDate = this.getDefaultDate(deliveryDate, shippingMethod);

    let minDate = new Date(defaultDate);
    minDate.setDate(minDate.getDate() - 1);
    minDate = this.adjustWeekend(minDate);

    const maxDate = new Date(defaultDate);

    const minText = this.formatShortDate(minDate);
    const maxText = this.formatShortDate(maxDate);

    if (minText === maxText) {
      return `receba em ${maxText}`;
    }

    return `receba entre ${minText} e ${maxText}`;
  }
}

if (!customElements.get("shipping-calculator")) {
  customElements.define("shipping-calculator", ShippingCalculator);
}
